import { useIsEditorContext } from "@editor-providers";
import { useSelf, useUpdateMyPresence } from "@liveblocks/react";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { CollaborationEnabledProvider } from "./CollaborationEnabledProvider";

const CURSOR_PRESENCE_THROTTLE_MS = 50;

type UserPresenceProviderProps = {
  children: ReactNode;
  id: string;
};

type CursorMessage = {
  id: string;
  text: string;
  createdAt: number;
  expiresAt: number;
};

type CursorPresence = {
  x: number;
  y: number;
  id: string;
  message?: CursorMessage;
};

export const UserPresenceProvider = ({
  children,
  id,
}: UserPresenceProviderProps) => {
  const isEditor = useIsEditorContext();
  if (!isEditor) return <>{children}</>;
  return (
    <CollaborationEnabledProvider fallback={children}>
      <ProviderContent id={id}>{children}</ProviderContent>
    </CollaborationEnabledProvider>
  );
};

const ProviderContent = ({ id, children }: UserPresenceProviderProps) => {
  const updateMyPresence = useUpdateMyPresence();
  const cursorMessage = useSelf(
    (me) => (me.presence.cursor as CursorPresence | null)?.message,
  );
  const lastPosition = useRef<{ x: number; y: number } | null>(null);
  const cursorMessageRef = useRef(cursorMessage);
  const pendingCursorRef = useRef<{ x: number; y: number; id: string } | null>(
    null,
  );
  const presenceTimeoutRef = useRef<number | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    cursorMessageRef.current = cursorMessage;
  }, [cursorMessage]);

  const flushPresence = useCallback(
    (cursor: { x: number; y: number; id: string } | null) => {
      if (!isMounted.current) return;
      try {
        updateMyPresence({
          cursor:
            cursor && cursorMessageRef.current
              ? { ...cursor, message: cursorMessageRef.current }
              : cursor,
        });
      } catch (error) {
        console.warn("Failed to update presence:", error);
      }
    },
    [updateMyPresence],
  );

  const updatePresence = useCallback(
    (cursor: { x: number; y: number; id: string } | null) => {
      if (!isMounted.current && cursor) return;

      if (!cursor) {
        pendingCursorRef.current = null;

        if (presenceTimeoutRef.current) {
          window.clearTimeout(presenceTimeoutRef.current);
          presenceTimeoutRef.current = null;
        }

        flushPresence(null);
        return;
      }

      if (!presenceTimeoutRef.current) {
        flushPresence(cursor);

        presenceTimeoutRef.current = window.setTimeout(() => {
          presenceTimeoutRef.current = null;
          const pendingCursor = pendingCursorRef.current;
          pendingCursorRef.current = null;

          if (!pendingCursor) return;

          flushPresence(pendingCursor);
        }, CURSOR_PRESENCE_THROTTLE_MS);
        return;
      }

      pendingCursorRef.current = cursor;
    },
    [flushPresence],
  );

  const transformPosition = useCallback(
    (cursor: { x: number; y: number }, element: HTMLElement) => {
      return {
        x: (cursor.x / element.clientWidth) * 100,
        y: (cursor.y / element.clientHeight) * 100,
        id,
      };
    },
    [id],
  );

  const onPointerLeave = useCallback(() => {
    if (!isMounted.current) return;
    lastPosition.current = null;
    updatePresence(null);
  }, [updatePresence]);

  const onPointerMove = useCallback(
    (event: MouseEvent) => {
      if (!isMounted.current) return;
      const element = event.currentTarget as HTMLElement;
      const rect = element.getBoundingClientRect();

      // Calculate position relative to the element's content box
      const position = {
        x: Math.max(0, Math.min(event.clientX - rect.left, rect.width)),
        y: Math.max(0, Math.min(event.clientY - rect.top, rect.height)),
      };

      // Only update if cursor is within the element's bounds
      if (
        position.x < 0 ||
        position.x > rect.width ||
        position.y < 0 ||
        position.y > rect.height
      ) {
        return onPointerLeave();
      }

      lastPosition.current = position;
      const transformed = transformPosition(position, element);
      updatePresence(transformed);
    },
    [transformPosition, updatePresence, onPointerLeave],
  );

  useEffect(() => {
    isMounted.current = true;
    const container = document.getElementById("content-sgKecQsAYKy7MX4");
    const element = container?.querySelector(`#slide-id-${id}`) as HTMLElement;
    if (!element) return;

    element.addEventListener("mousemove", onPointerMove);
    element.addEventListener("mouseleave", onPointerLeave);

    return () => {
      element.removeEventListener("mousemove", onPointerMove);
      element.removeEventListener("mouseleave", onPointerLeave);
      // Clean up presence on unmount
      updatePresence(null);
      isMounted.current = false;
    };
  }, [id, onPointerMove, onPointerLeave, updatePresence]);

  return <>{children}</>;
};
